// Visão auxiliar "planilha" da programação (pedido da torre, 30/10/2026).
// Reproduz, coluna a coluna, a planilha que a operação usa no dia a dia:
//   VEÍCULO · DATA/HORA · TRANSPORTE · PESO · PÁTIO · CLIENTE · CIDADE ·
//   URGENTE · FRETE (Normal / Frete Especial)
// e acrescenta ao fim o que a planilha não tinha coluna: situação,
// prioridade e crítica. A Torre de Controle continua sendo a tela principal.
import { chaveColeta } from "./carga.js";

export interface LinhaCarga {
  id: number;
  empresa?: string | null;
  fase?: string | null;
  numero_ordem?: string | null;
  tipo_veiculo?: string | null;
  data_hora_coleta?: string | null;
  data_hora_solicitada?: string | null;
  peso?: number | string | null;
  patio_coleta?: string | null;
  cliente_entrega?: string | null;
  cidade_entrega?: string | null;
  status_carga?: string | null;
  motorista?: string | null;
  urgente?: boolean | null;
  prioridade?: boolean | null;
  critica?: boolean | null;
  frete_especial?: string | null;
}

/**
 * Data/hora da linha: a coleta CONFIRMADA pela torre; sem ela, o horário
 * SOLICITADO (marcado como tal — pedido não é compromisso).
 */
export function horarioLinha(c: LinhaCarga): { chave: string | null; solicitado: boolean } {
  const coleta = chaveColeta(c.data_hora_coleta);
  if (coleta) return { chave: coleta, solicitado: false };
  const pedido = chaveColeta(c.data_hora_solicitada);
  return { chave: pedido, solicitado: !!pedido };
}

/** "2026-10-27 04:00" → "27/10/2026 04:00:00", como a planilha exibe. */
export function fmtDataHoraPlanilha(chave: string | null): string {
  if (!chave || !/^\d{4}-\d{2}-\d{2}/.test(chave)) return "—";
  const hora = chave.slice(11, 16) || "00:00";
  return `${chave.slice(8, 10)}/${chave.slice(5, 7)}/${chave.slice(0, 4)} ${hora}:00`;
}

/** Peso com 3 casas e vírgula ("35,324"), igual à planilha. */
export function fmtPesoPlanilha(v: unknown): string {
  const n = typeof v === "number" ? v : Number(v);
  if (v == null || v === "" || !Number.isFinite(n)) return "—";
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

/** Sequência da planilha: data/hora crescente, sem horário no fim. */
export function ordenarPlanilha<T extends LinhaCarga>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const ka = horarioLinha(a).chave;
    const kb = horarioLinha(b).chave;
    if (ka !== kb) return ka == null ? 1 : kb == null ? -1 : ka.localeCompare(kb);
    return a.id - b.id;
  });
}

/** Dias presentes (para o filtro de dia), em ordem. */
export function diasDaPlanilha(rows: LinhaCarga[]): string[] {
  return [...new Set(rows.map((r) => horarioLinha(r).chave?.slice(0, 10)).filter((d): d is string => !!d))].sort();
}

/** Coluna FRETE da planilha: "Frete Especial" ou "Normal". */
export function freteLinha(c: LinhaCarga): string {
  const fe = (c.frete_especial ?? "").trim();
  if (!fe) return "Normal";
  return fe === "FRETE ESPECIAL" ? "Frete Especial" : fe;
}

/** Pátio composto ("P14/GIO", "P07/PEX1/PEX1") ganha destaque, como na planilha. */
export function patioComposto(patio: unknown): boolean {
  return String(patio ?? "").split("/").map((s) => s.trim()).filter(Boolean).length > 1;
}

export const COLUNAS_PLANILHA = [
  "Veículo", "Data/Hora", "Transporte", "Peso", "Pátio", "Cliente", "Cidade",
  "Urgente", "Frete", "Situação", "Prioridade", "Crítica",
] as const;

/** Valores de uma linha na ordem de COLUNAS_PLANILHA (tela e cópia para Excel). */
export function valoresLinha(c: LinhaCarga): string[] {
  const h = horarioLinha(c);
  return [
    c.tipo_veiculo || "—",
    fmtDataHoraPlanilha(h.chave),
    c.numero_ordem || "—",
    fmtPesoPlanilha(c.peso),
    c.patio_coleta || "—",
    c.cliente_entrega || "—",
    c.cidade_entrega || "—",
    c.urgente ? "Urgente" : "-",
    freteLinha(c),
    c.status_carga || (c.motorista ? "Agendada" : "Disponível"),
    c.prioridade ? "Prioridade" : "-",
    c.critica ? "Crítica" : "-",
  ];
}

/** TSV com cabeçalho: cola direto no Excel/Google Sheets mantendo as colunas. */
export function paraTsv(rows: LinhaCarga[]): string {
  const limpa = (s: string) => s.replace(/[\t\r\n]+/g, " ");
  return [COLUNAS_PLANILHA.join("\t"), ...rows.map((r) => valoresLinha(r).map(limpa).join("\t"))].join("\n");
}
